import { useState } from "react";
import { useDebouncedList } from "../../lib/useDebouncedList";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { fmtDate } from "../../lib/api";
import { btnGhost, inputCls } from "../../components/ui";

const ENTITIES = ["", "user", "order", "receipt", "store", "product", "settings", "bank_account", "delivery", "courier", "topup", "ledger"];
const json = (v) => JSON.stringify(v, null, 1);

function Entry({ h }) {
  const [open, setOpen] = useState(false);
  const extra = h.before || h.after || h.reason || Object.keys(h.details || {}).length;
  return (
    <div className="p-2.5 text-xs" data-testid={`audit-entry-${h.id}`}>
      <button type="button" disabled={!extra} onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left">
        <span className="text-gray-400">{fmtDate(h.created_at)}</span>
        <strong>{h.action}</strong><span>· {h.actor_email} ({h.actor_role}) · {h.entity} {String(h.entity_id || "").slice(0, 8)}</span>
        {h.reason && <span className="rounded-full bg-amber-100 px-2 text-amber-800">motivo</span>}
        {extra ? <ChevronDown className={`ml-auto h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> : null}
      </button>
      {open && <div className="mt-2 grid gap-2 sm:grid-cols-3" data-testid={`audit-detail-${h.id}`}>
        {h.reason && <p className="sm:col-span-3"><span className="font-semibold">Motivo:</span> {h.reason}</p>}
        {h.before && <pre className="overflow-auto rounded-lg bg-red-50 p-2">Antes: {json(h.before)}</pre>}
        {h.after && <pre className="overflow-auto rounded-lg bg-emerald-50 p-2">Depois: {json(h.after)}</pre>}
        {Object.keys(h.details || {}).length > 0 && <pre className="overflow-auto rounded-lg bg-gray-50 p-2">Detalhes: {json(h.details)}</pre>}
      </div>}
    </div>
  );
}

export default function AdminAudit() {
  const [f, setF] = useState({ entity: "", action: "", actor: "", page: 1 });
  const [data] = useDebouncedList("/admin/audit", f, { items: [], total: 0, limit: 50 });
  const pages = Math.max(1, Math.ceil(data.total / data.limit));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, page: 1 });
  return (
    <div className="flex flex-col gap-3" data-testid="admin-audit">
      <div className="bo-card flex flex-wrap gap-3 p-4">
        <select value={f.entity} onChange={set("entity")} className={`${inputCls} w-auto`} data-testid="audit-entity-filter">{ENTITIES.map((e) => <option key={e} value={e}>{e || "Todas as entidades"}</option>)}</select>
        <input placeholder="Ação (ex.: user.suspend)" value={f.action} onChange={set("action")} className={`${inputCls} sm:w-64`} data-testid="audit-action-filter" />
        <input placeholder="Email de quem alterou" value={f.actor} onChange={set("actor")} className={`${inputCls} sm:w-64`} data-testid="audit-actor-filter" />
      </div>
      <p className="text-xs text-gray-500">O registo de auditoria é só de leitura e não pode ser apagado através do painel.</p>
      <div className="bo-card divide-y divide-gray-50" data-testid="admin-audit-list">{data.items.map((h) => <Entry key={h.id} h={h} />)}</div>
      <div className="flex items-center justify-end gap-2 text-sm">
        <span className="text-gray-500">{data.total} registos · página {f.page} de {pages}</span>
        <button disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} className={btnGhost} data-testid="audit-prev"><ChevronLeft className="h-4 w-4" /></button>
        <button disabled={f.page >= pages} onClick={() => setF({ ...f, page: f.page + 1 })} className={btnGhost} data-testid="audit-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
